<script setup lang="ts">
import { useEventListener } from '@vueuse/core'
import { ActionFields } from '~/components/cms/Action/actionHelpers'
import { PanelProps } from '~/components/cms/PanelSwitcher.vue'
import { QuickLinksProp } from '~/components/cms/UtilityPanel/QuickLinks.vue'

type Props = {
  seo?: SEOProps
  categoryId?: string
  preListContent?: PanelProps[]
  postListContent?: PanelProps[]
  quickLinks?: QuickLinksProp
  inlineListTeaserCard1?: any[]
  inlineListTeaserCard2?: any[]
  inlineListTeaserCard3?: any[]
}

type BrazeBannerConfig = {
  placementIds?: string[]
  supportingData?: any
}

type BrazeBannerItem = { brazeBanner: BrazeBannerConfig }

const props = defineProps<Props>()
const emits = defineEmits<{
  (e: 'update-breadcrumbs', breadcrumbs: { text: string; url: string }[]): void
}>()
const route = useRoute()
const preferencesStore = usePreferencesStore()
const brazeBanner = computed<BrazeBannerConfig | undefined>(() => preferencesStore.site?.plpBrazeBanner)
const { load: loadProducts } = useConstructor()
const { trackBrowseResultsLoaded } = useConstructorTracker()
const { getFacetsFromURL, getFormattedFilters, getGrouped, categoryIdToUrl } = useSiteUrl()
const loading = ref(true)
const pageNumber = ref(1)
const products = ref<(Omit<ProductTileProps, 'index'> | BrazeBannerItem)[]>([])

// A real product tile, i.e. neither an inline teaser card (has `fields`) nor a Braze Banner marker.
const isRealProduct = (product: any): product is Omit<ProductTileProps, 'index'> =>
  'name' in product && !('fields' in product) && !('brazeBanner' in product)
const facets = ref()
const totalProducts = ref(0)
const sortOptions = ref<SortOpion[]>([])
const activeLinks = ref<ActionFields[]>([])
const constructorEventData = ref()
const { t, locale } = useI18n()
provide('gaOptions', {
  group: 'plp',
})

const addUrlParam = (url: string | undefined, key: string, value: string): string => {
  if (!url) return ''

  const [baseUrl, queryString] = url.split('?')
  const searchParams = new URLSearchParams(queryString)
  searchParams.set(key, value)

  return `${baseUrl}?${searchParams.toString()}`
}

const decoratedQuickLinks = computed<{ fields?: QuickLinksProp['fields'] }>(() => ({
  fields: {
    ...props.quickLinks?.fields,
    actions: props.quickLinks?.fields?.actions
      // filter out Draft actions, which don't have fields
      ?.filter((action) => action?.fields && personalizationControl(action.fields.personalizationTags))
      .map((action) => {
        const link = action?.fields
        if (!link) return action
        return {
          ...action,
          fields: {
            ...link,
            url: addUrlParam(link.url, 'ref', 'quicklink'),
          },
        }
      }),
  },
}))

// A slot can hold member and non-member variants; the grid has room for one tile per page.
const teaserCardForPage = (page: number) => {
  const cards = [props.inlineListTeaserCard1, props.inlineListTeaserCard2, props.inlineListTeaserCard3][page - 1]
  return cards?.find((card) => card?.fields && personalizationControl(card.fields.personalizationTags))
}

const hasTeaserCard = computed(() => teaserCardForPage(pageNumber.value)?.fields)

const isProductTeaser = computed(
  () => hasTeaserCard.value && hasTeaserCard.value.supportingData?.home?.variant?.value === 'product',
)

const itemsPerPage = computed(() => (isProductTeaser.value ? 23 : hasTeaserCard.value ? 22 : 24))

const someQuickLinksHaveImage = computed(() =>
  decoratedQuickLinks.value.fields?.actions?.some((action) => action.fields?.quickLinkPicture),
)

const updateActiveLinks = () => {
  activeLinks.value = []
  decoratedQuickLinks.value.fields?.actions?.forEach((action) => {
    if (!action?.fields?.url) return
    const [baseUrl, queryString] = action.fields.url.split('?')
    const relativeUrl = baseUrl.replace(/https?:\/\/[^/]+/, '')
    const deLocaledPath = route.path.replace(/^fr\//, '')
    const params = new URLSearchParams(queryString)
    params.delete('ref')

    if (deLocaledPath !== relativeUrl) return

    const allParamsMatch = [...params.entries()].every(([key, value]) => {
      const currentValue = route.query[key]
      return Array.isArray(currentValue) ? currentValue.includes(value) : currentValue === value
    })
    const currentUrlHasParams = Object.keys(route.query).filter((k) => k !== 'ref').length > 0

    if (allParamsMatch || (someQuickLinksHaveImage.value && !currentUrlHasParams)) {
      activeLinks.value.push(action)
    }
  })
}

updateActiveLinks()

// TODO - Seems redundant. Remove later
const onChangeCategory = (action: ActionFields) => {
  //   activeLinks.value = []
  //   activeLinks.value.push(action)
}

const getConstructorRequestData = () => {
  const facetsFromUrl = getFacetsFromURL()
  const { sortByValue, sortOrderValue } = facetsFromUrl
  let { filters } = facetsFromUrl
  filters = filters ? getFormattedFilters(filters) : {}
  let price = filters?.price
  if (price?.[0]) {
    price = [price[0].replace(/[()]/g, '').replace('..', '-')]
  }
  return { filters, price, sortByValue, sortOrderValue }
}

const initPage = async () => {
  loading.value = true
  pageNumber.value = 1
  const page = await loadPage()
  const { productsResult, facetsResult, sortOptionsResults, categoriesResult = [] } = page

  if (locale.value === 'fr') {
    const links =
      categoriesResult?.[0]?.parents?.map((parent) => ({
        text: parent.group_id === 'root' ? t('home_text') : parent.display_name,
        url: parent.group_id === 'root' ? '/' : `/${categoryIdToUrl(parent.group_id)}`,
      })) || []
    links.push({
      text: categoriesResult?.[0]?.display_name || '',
      url: `/${categoryIdToUrl(categoriesResult?.[0]?.group_id || '')}`,
    })
    emits('update-breadcrumbs', links)
  }

  sortOptions.value = sortOptionsResults || []
  totalProducts.value = facetsResult?.data.total || 0
  facets.value = facetsResult ? getGrouped(facetsResult) : undefined
  products.value = insertInlineContent(productsResult || [])
  loading.value = false

  trackProductListRender({
    list: props.categoryId || '',
    location: 'product listing page',
    group: 'product list',
    label: props.categoryId,
    products: (productsResult || []).filter(isRealProduct).map((product) => product.productData),
    locale: locale.value,
  })

  const plpName = props.seo?.title || ''
  useSchemaOrg().initPlpSchema(plpName, productsResult || [])
}

const loadPage = async (offset = 0) => {
  const { filters, price, sortByValue, sortOrderValue } = getConstructorRequestData()
  const sorting = {
    sortBy: sortByValue,
    sortOrder: sortOrderValue,
  }
  const page = (await loadProducts(props.categoryId, {
    offset,
    resultsPerPage: itemsPerPage.value,
    filters: {
      ...filters,
      price,
    },
    ...sorting,
  })) || { productsResult: [], facetsResult: { data: { total: 0 } }, sortOptionsResults: [], categoriesResult: [] }
  constructorEventData.value = {
    filterName: 'group_id',
    filterValue: props.categoryId || '',
    numResultsPerPage: itemsPerPage.value,
    resultCount: page.productsResult.length || 0,
    resultPage: pageNumber.value,
    selectedFilters: filters,
    ...sorting,
  }
  trackBrowseResultsLoaded({
    url: window.location.href,
    items: mapConstructorItemIdFromSearch(page.productsResult || []),
    ...constructorEventData.value,
  })

  return page
}

const loadNextPage = async () => {
  loading.value = true
  pageNumber.value++
  const positionOffset = products.value.filter(isRealProduct).length
  const page = await loadPage(positionOffset)
  const productsResult = page.productsResult || []
  products.value = products.value.concat(insertInlineContent(productsResult))
  loading.value = false

  trackProductListRender({
    list: props.categoryId || '',
    location: 'product listing page',
    group: 'product list',
    label: props.categoryId,
    products: productsResult.filter(isRealProduct).map((product) => product.productData),
    positionOffset,
    locale: locale.value,
  })

  // Update PLP schema with full accumulated product list
  const plpName = props.seo?.title || ''
  const allLoadedProducts = products.value.filter(isRealProduct)
  useSchemaOrg().initPlpSchema(plpName, allLoadedProducts)
}

const saveScrollPosition = () => {
  const scrollPosition =
    window.scrollY ||
    window.pageYOffset ||
    document.body.scrollTop + ((document.documentElement && document.documentElement.scrollTop) || 0)

  window.history.replaceState(
    {
      ...window.history.state,
      plpPageNumber: pageNumber.value,
      plpScrollPosition: scrollPosition,
    },
    '',
  )
  localStorage.setItem('plpScrollPosition', scrollPosition.toString()) // Safari hack
}

const restoreScrollPosition = async () => {
  let plpScrollPosition = window.history.state?.plpScrollPosition
  const plpPageNumber = window.history.state?.plpPageNumber

  if (plpPageNumber > 1) {
    for (let i = 1; i < plpPageNumber; i++) {
      await loadNextPage()
    }
  }

  if (plpScrollPosition) {
    window.scrollTo({ top: parseInt(plpScrollPosition), behavior: 'smooth' })
  }
}

// Insert the appropriate inline content (teaser card + Braze Banner) based on the page number.
// The teaser card uses the inline grid slot after the first 8 products on each page. The Braze
// Banner (global, one per PLP) shares that slot on the first page only, positioned directly above
// the teaser tile.
const insertInlineContent = (products: Omit<ProductTileProps, 'index'>[]) => {
  let productList: (Omit<ProductTileProps, 'index'> | BrazeBannerItem)[] = [...products]
  if (products.length > 7) {
    const teaserCard = teaserCardForPage(pageNumber.value)

    if (teaserCard) {
      productList.splice(8, 0, teaserCard)
    }

    // Insert last so it lands at index 8, directly above the teaser tile.
    // Only one Braze Banner per PLP: insert on the first page only.
    if (pageNumber.value === 1 && brazeBanner.value?.placementIds?.length) {
      productList.splice(8, 0, { brazeBanner: brazeBanner.value })
    }
  }
  return productList
}

// Site preferences are loaded fire-and-forget at the app root, so `brazeBanner` can still be
// undefined when the first page is built (the product request can win the race). Watch for the
// preference becoming available and insert the marker reactively so the global PLP banner still
// appears. Guarded to run once, only on the first page, mirroring insertInlineContent()'s placement.
watch(brazeBanner, (banner) => {
  if (pageNumber.value !== 1 || !banner?.placementIds?.length) return
  if (products.value.some((item) => 'brazeBanner' in item)) return
  if (products.value.filter(isRealProduct).length > 7) {
    products.value.splice(8, 0, { brazeBanner: banner })
  }
})

onBeforeMount(async () => {
  await initPage()
  useEventListener(window, 'pagehide', saveScrollPosition)
  useEventListener(window, 'pageshow', (event) => {
    if (event.persisted) {
      const plpScrollPosition = localStorage.getItem('plpScrollPosition')
      if (plpScrollPosition) {
        window.scrollTo({ top: parseInt(plpScrollPosition), behavior: 'smooth' })
        localStorage.removeItem('plpScrollPosition')
      }
    }
  })
  await restoreScrollPosition()
})

watch(
  () => route.query,
  () => {
    if (!someQuickLinksHaveImage.value) {
      updateActiveLinks()
    }
    return initPage()
  },
)

useHead(
  getHeaderData({
    seo: props?.seo,
    locale: useI18n().locale.value,
    path: route.path || '',
  }),
)
</script>

<template>
  <div class="product-listing-page">
    <PanelSwitcher v-for="(panel, i) in preListContent" :key="i" v-bind="panel" :priority="i === 0" />

    <section class="container quick-links" v-if="decoratedQuickLinks.fields?.actions?.length">
      <QuickLinks
        v-bind="decoratedQuickLinks"
        :active-links="activeLinks"
        :disable-active-state="decoratedQuickLinks.fields?.disableActiveState"
        @change-category="onChangeCategory"
      />
    </section>

    <ProductList
      :products
      :facets
      :totalProducts
      :sortOptions
      :loading
      :constructorEventData
      :list-name="props.categoryId || ''"
      @next-page="loadNextPage"
    />

    <section class="container product-listing-page__post-list-content">
      <PanelSwitcher v-for="(panel, i) in postListContent" :key="i" v-bind="panel" />
    </section>
  </div>
</template>

<style lang="postcss">
.product-listing-page {
  /* Any component right before the product-list gets the same bottom spacing as quick-links */
  & > :has(+ .product-list):not(.container.container.quick-links):not([class*='--no-margin-bottom']) {
    margin-bottom: var(--space-90);
  }
  .container.quick-links {
    margin-bottom: var(--space-90);
  }

  .hero-panel__content {
    max-width: 100%;

    .hero-panel__text {
      max-width: 26rem;
    }
  }

  .hero-panel-standard-content-only {
    padding-top: var(--space-50);
    padding-bottom: var(--space-70);
  }
  .product-listing-page__post-list-content {
    .container {
      max-width: 40rem;
    }
  }

  .braze-banner {
    padding-inline: 0;
  }
}
</style>
